import type { Address } from '@/common/domain/Address';
import { type ApiEntities, type ApiEntity, mapApiEntities, mapApiEntity } from '@/common/domain/api';
import type { Contact } from '@/common/domain/Contact';
import type { VehicleType } from '@/fleet/domain/vehicle';
import type { Group } from '@/group/domain/group';
import type { ReservationClass } from '@/subscription/domain/resaClass';

export interface Operations {
    defaultLanguage: string;
    chargingPointsNumber: number;
    placesCapacity: number;
    GMThour: number;
    plateNumberReader: boolean;
    maxCarLength?: number,
    maxCarWidth?: number,
    maxCarHeight?: number,
    maxCarWeight?:number,
    // Does the parking system require a pin code in addition to the plate number.
    pinCodeRequired?: boolean,
    // Average retrieve time per vehicle, in seconds; used to estimate retrieve duration.
    averageRetrieveTime?: number
}

/// A parking is considered offline when its last heartbeat is older than this.
export const PARKING_HEARTBEAT_TIMEOUT_MS = 15 * 60 * 1000;

/// How often the live parking status (heartbeat, queue) is re-polled while the dashboard is visible.
export const PARKING_STATUS_POLL_INTERVAL_MS = 60 * 1000;

// Live operational status of a parking, populated from the `statuses` component.
export interface ParkingStatuses {
    lastHeartbeat?: string;
    queue: string[];
}

// True when a heartbeat exists and is within PARKING_HEARTBEAT_TIMEOUT_MS of now.
export const isParkingOnline = (statuses?: ParkingStatuses, now: number = Date.now()): boolean => {
    if (!statuses?.lastHeartbeat) return false;
    const heartbeat = Date.parse(statuses.lastHeartbeat);
    return !Number.isNaN(heartbeat) && now - heartbeat <= PARKING_HEARTBEAT_TIMEOUT_MS;
};

export enum LoungeState {
    Available='available',
    OutOfOrder='outOfOrder',
    InUse='inUse'
}

export enum ChargerConnector {
    Type1='Type 1',
    Type2='Type 2',
    CCS='CCS',
    CCS1='CCS1',
    CCS2='CCS2',
    CHAdeMO='CHAdeMO',
    GBT='GB/T',
}

interface ChargerType {
    id?: number;
    name: string;
    maxCharge: number;
    type: ChargerConnector
}

export type { ChargerType };

export interface Lounge {
    id?: string,
    name: string;
    refAutomatisme?: string;
    accessibility: boolean;
    state: LoungeState;
}

export interface ApiPlaceType {
    id?: number;
    name: string;
    capacity: number;
    availableLounges?: ApiEntities<Lounge>;
    maxDepth?: number;
    maxWidth?: number;
    maxHeight?: number;
    maxWeight?:number;
    chargerType: { data: ApiEntity<ChargerType>};
    vehicleTypes?: ApiEntities<VehicleType>;
}


export interface PlaceType {
    id?: number;
    name: string;
    chargerType?: ChargerType;
    vehicleTypes?: VehicleType[];
    capacity: number;
    availableLounges?: Lounge[];
    maxDepth?: number;
    maxWidth?: number;
    maxHeight?: number;
    maxWeight?:number;
}

export interface Parking {
    id?: number;
    documentId?: string;
    name: string;
    email: string;
    phone: string;
    lounges: Lounge[];
    placeTypes: PlaceType[];
    address: Address;
    operations: Operations;
    commercialInfos: {
        companyNumber: string;
        VATlegislation: string;
    };
    contact: Contact;
    cover?: string;
    managementCover?: string;
    pincodeRequired: boolean;
    averageRetrieveTime?: number;
    statuses?: ParkingStatuses;
}

export interface ParkCompPair {
    parking: Parking;
    company: Group;
    resaClass: ReservationClass;
}

export interface ParkingWithCompanies {
    parking: Parking,
    companies: Group[],
    totalEndUsers: number, // nb of member of each comapnies linked to the current parking
    totalLocalAdmins: number, // nb of member of superadmingroup for each groupconfig for the curr parking
}
export interface ApiParkingAttributes {
    id?: number;
    documentId?: string;
    name: string;
    email: string;
    phone: string;
    lounges: ApiEntities<Lounge>;
    placeTypes: ApiPlaceType[];
    address: Address;
    operations: Operations;
    commercialInfos: {
        companyNumber: string;
        VATlegislation: string;
    };
    contact: Contact;
    cover?: string;
    managementCover?: string;
    pincodeRequired: boolean;
    averageRetrieveTime?: number;
    statuses?: { lastHeartbeat?: string; queue?: unknown };
}


const parkingToApiParking = (parking: Parking): unknown => {
    const {name, email, phone, address, commercialInfos, contact, cover, managementCover} = parking;
    return {
        name,
        email,
        phone,
        address,
        commercialInfos,
        contact,
        cover,
        managementCover,
        operations: {
            ...parking.operations,
            maxCarLength: parking.operations.maxCarLength ? parking.operations.maxCarLength : null,
            maxCarWidth: parking.operations.maxCarWidth ? parking.operations.maxCarWidth : null,
            maxCarHeight: parking.operations.maxCarHeight ? parking.operations.maxCarHeight : null,
            maxCarWeight: parking.operations.maxCarWeight ? parking.operations.maxCarWeight : null,
            pinCodeRequired: parking.operations.pinCodeRequired ?? false,
            averageRetrieveTime: parking.operations.averageRetrieveTime ? Number(parking.operations.averageRetrieveTime) : null
        },
        lounges: parking.lounges?.map(item => { 
            return { 
                ...item,
                id: item.id && !/new_/.exec(String(item.id)) ? item.id : undefined,
            };
        }),
        placeTypes: parking.placeTypes?.map(item => { 
            return { 
                ...item,
                id: item.id && item.id > 0 ? item.id : undefined,
                capacity: Number(item.capacity),
                maxDepth: Number(item.maxDepth),
                maxHeight: Number(item.maxHeight),
                maxWeight: Number(item.maxWeight),
                maxWidth: Number(item.maxWidth),
                availableLounges: item.availableLounges?.map(ltem => {
                    return ltem.id;
                }),
                vehicleTypes: item.vehicleTypes?.map(vtem => {
                    return vtem.id;
                })
            };
        })
    };
};
const apiPlaceTypeToPlaceType = (placeType: ApiPlaceType): PlaceType => {
    const pT: PlaceType = {
        ...placeType,
        chargerType: undefined,
        vehicleTypes: [],
        availableLounges: []
    };

    if (placeType.vehicleTypes) {
        pT.vehicleTypes = mapApiEntities<VehicleType>(placeType.vehicleTypes);
    } else {
        delete pT.vehicleTypes;
    }

    if (placeType.chargerType) {
        pT.chargerType = mapApiEntity<ChargerType>(placeType.chargerType.data);
    } 
    if (placeType.availableLounges) {
        pT.availableLounges = mapApiEntities<Lounge>(placeType.availableLounges);
    } 
    return pT;
};


const apiStatusesToStatuses = (statuses?: ApiParkingAttributes['statuses']): ParkingStatuses | undefined => {
    if (!statuses) return undefined;
    return {
        lastHeartbeat: statuses.lastHeartbeat,
        queue: Array.isArray(statuses.queue) ? statuses.queue.map(String) : [],
    };
};

const apiParkingToParking = (apiParking: ApiEntity<ApiParkingAttributes>): Parking => {
    // Use mapApiEntity to handle the Strapi v5 flat structure
    const mapped = mapApiEntity<ApiParkingAttributes>(apiParking);
    const { id: _mappedId, lounges, placeTypes, statuses, ...restAttributes } = mapped;

    return ({
        ...restAttributes,
        id: apiParking.id,
        lounges: lounges ? mapApiEntities<Lounge>(lounges) : [],
        placeTypes: placeTypes ? placeTypes.map(apiPlaceTypeToPlaceType) : [],
        statuses: apiStatusesToStatuses(statuses),
    });
};

const apiParkingsToParkings = (apiParkings: ApiEntities<ApiParkingAttributes> | ApiEntity<ApiParkingAttributes>[]): Parking[] => {
    return ((apiParkings as any).data ? (apiParkings as ApiEntities<ApiParkingAttributes>).data : (apiParkings as ApiEntity<ApiParkingAttributes>[])).map((apiParking: ApiEntity<ApiParkingAttributes>) => {
        return apiParkingToParking(apiParking);
    });
};

const useParking = {
    apiParkingToParking,
    apiParkingsToParkings,
    parkingToApiParking,
};

export default useParking;